import { Checkbox, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAddress } from 'viem'
import { sendTransaction } from 'viem/actions'

import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { BLANK_ADDRESS, ELECTIONS_ADDRESS } from '@/constants/blockchain'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import OMMAElection from '@/contracts/OMMAElection'
import { ContractCall, useChainFunctions } from '@/hooks/useChain'
import useL1NS from '@/hooks/useL1NS'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

import RootButton from '../../Buttons/RootButton'
import UserDetails from '../../Details/UserDetails/UserDetails'
import { checkInputHandleAddress } from '../../Identity/rules'
import { AtIcon, CheckedIcon, InvalidIcon, SpecialInputText } from '../../SpecialInputText'
import { DEBOUNCE_USERNAME_RESOLVE_TIME } from '../../Transfer/Send'
import ModalSkeleton from '../ModalSkeleton'

interface NominationModalProps {
  close: () => void
}

const NominateModal: React.FC<NominationModalProps> = ({ close }) => {
  const { t } = useTranslation()
  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)
  const { getL1nsName, getL1nsAddress } = useL1NS()
  const {
    vote,
    hasBeenNominated,
    // Settings
    minVoteBalance,
  } = useGovernanceContext()

  // User can nominate only if has more vote balance than the minimum required
  const canNominate = useMemo(() => vote.value >= minVoteBalance.value, [minVoteBalance, vote])

  // modal
  const [openedConfirmation, { open: openConfirmation, close: closeConfirmation }] = useDisclosure()

  const [destinationAddress, setDestinationAddress] = useState({
    value: '',
    touched: false,
    error: '',
  })
  const recipientAddressRef = useRef<string | null>(null)
  // handle vs address
  const [validSendAddress, setValidSendAddress] = useState<boolean>(false)
  const [isHandle, setIsHandle] = useState<boolean>(false)
  const [handleCheckStatus, setHandleCheckStatus] = useState({
    input: '',
    message: '',
    verified: false,
  })
  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  // Confirm checkbox
  const [confirmCheckbox, setConfirmCheckbox] = useState<boolean>(false)
  const isAddressNeedsConfirmation = useMemo(
    () => !confirmCheckbox && !isHandle,
    [confirmCheckbox, isHandle]
  )

  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    if (!recipientAddressRef.current) {
      return undefined
    }
    return [
      {
        args: {
          address: ELECTIONS_ADDRESS,
          abi: OMMAElection,
          functionName: 'nominate',
          args: [recipientAddressRef.current],
        },
      },
    ]
  }, [recipientAddressRef.current])

  const handleNominate = useCallback(async () => {
    if (!(contractCalls && contractCalls.length > 0)) return
    try {
      setIsLoading(true)
      await writeContract(contractCalls[0].args)
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [contractCalls, writeContract])

  // Left Icon showing validation status
  const iconLeft = useMemo(
    () =>
      handleCheckStatus.verified ? (
        validSendAddress ? (
          <CheckedIcon />
        ) : (
          <InvalidIcon />
        )
      ) : (
        <AtIcon />
      ),
    [validSendAddress, handleCheckStatus]
  )

  // Handle Username/Address Validation:
  // check if username exists, set recipient address, and validate send address
  const handleCheckStatusAction = useCallback(async () => {
    if (isHandle) {
      const reverseAddress = await getL1nsAddress(destinationAddress.value)
      if (reverseAddress && reverseAddress !== BLANK_ADDRESS) {
        setHandleCheckStatus({
          input: destinationAddress.value,
          message: `${reverseAddress}`,
          verified: true,
        })
        // Check if user has been nominated if it's a valid username
        const beenNominated = hasBeenNominated(reverseAddress)
        if (beenNominated) {
          setDestinationAddress({
            value: destinationAddress.value,
            touched: true,
            error: 'User has already been nominated',
          })
          setValidSendAddress(false)
        } else {
          recipientAddressRef.current = reverseAddress
          setValidSendAddress(true)
        }
      } else {
        // If username is not found, set the error message
        setHandleCheckStatus({
          input: destinationAddress.value,
          message: '',
          verified: true,
        })
        setValidSendAddress(false)
        setDestinationAddress({
          value: destinationAddress.value,
          touched: true,
          error: 'Username not found',
        })
      }
    } else {
      const reverseName = await getL1nsName(destinationAddress.value)
      const beenNominated = hasBeenNominated(destinationAddress.value)
      console.log('hasBeenNominated', hasBeenNominated, reverseName, destinationAddress.value)
      if (beenNominated) {
        setDestinationAddress({
          value: destinationAddress.value,
          touched: true,
          error: 'User has already been nominated',
        })
        setValidSendAddress(false)
      } else {
        if (reverseName) {
          setHandleCheckStatus({
            input: destinationAddress.value,
            message: `${reverseName}`,
            verified: true,
          })
        } else {
          setHandleCheckStatus({
            input: destinationAddress.value,
            message: '',
            verified: true,
          })
        }
        recipientAddressRef.current = destinationAddress.value
        setValidSendAddress(true)
      }
    }
  }, [destinationAddress.value, getL1nsAddress, getL1nsName, isHandle, sendTransaction])

  const resetDestinationHandle = useCallback(() => {
    setHandleCheckStatus({
      input: '',
      message: '',
      verified: false,
    })
    setDestinationAddress({
      value: '',
      touched: false,
      error: '',
    })
    setValidSendAddress(false)
  }, [setHandleCheckStatus, setDestinationAddress])

  const handleDestinationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target
      resetDestinationHandle()
      setIsHandle(!isAddress(value))
      setConfirmCheckbox(false)
      const destinationError = checkInputHandleAddress(t, value, !isAddress(value))
      setDestinationAddress({
        value: value.toLowerCase(),
        touched: true,
        error: destinationError,
      })
      setValidSendAddress(false)
      setHandleCheckStatus({
        input: value.toLowerCase(),
        message: '',
        verified: false,
      })
      //   setSendingTx(null)
    },
    [t]
  )

  const handleCloseConfirmationCancel = useCallback(() => {
    setError('')
    closeConfirmation()
  }, [closeConfirmation])

  const handleCloseConfirmation = useCallback(() => {
    setError('')
    closeConfirmation()
    close()
  }, [closeConfirmation, close])

  // Resolve username from destination changes with debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (destinationAddress.touched && destinationAddress.error === '') {
        handleCheckStatusAction()
      }
    }, DEBOUNCE_USERNAME_RESOLVE_TIME)

    return () => {
      clearTimeout(handler)
    }
  }, [destinationAddress])

  return (
    <>
      <Stack>
        <Text>
          {t(
            'components.governance.nomination.text1',
            'You need at least {{minBalance}} vote power to nominate a candidate. You can only nominate one candidate per election.',
            {
              minBalance: minVoteBalance.display,
            }
          )}
        </Text>
        <SpecialInputText
          placeholder={t(
            'components.send.placeholderAddressAndUsername',
            'Enter address or username'
          )}
          value={destinationAddress.value}
          error={
            !canNominate
              ? `You need at least ${minVoteBalance.display} vote power to nominate`
              : destinationAddress.touched && destinationAddress.error !== ''
                ? destinationAddress.error
                : null
          }
          borderStatus={
            destinationAddress.touched
              ? destinationAddress.error
                ? 'error'
                : 'success'
              : 'default'
          }
          iconLeft={iconLeft}
          onChange={handleDestinationChange}
          tipLeft={t(
            'components.send.sentToTipAddressAndUsername',
            'Enter your recipient’s @username or 0x/wallet address'
          )}
          tipRight={t('components.send.sentToTipRight', 'Available: {{vote}} VOTE', {
            vote: vote.fullDisplay,
          })}
        />
        {!isHandle && handleCheckStatus.input !== '' && !destinationAddress.error && (
          <Checkbox
            checked={confirmCheckbox}
            onChange={() => setConfirmCheckbox(!confirmCheckbox)}
            label={t(
              'components.send.checkboxAddress',
              'Confirm that you want to nominate this address'
            )}
          />
        )}

        <Stack style={{ flexDirection: 'row', width: '100%' }}>
          <RootButton style1={false} secondary expand onClick={close}>
            {t('components.governance.modal.nomination.close', 'Close')}
          </RootButton>
          <RootButton
            style1={false}
            expand
            onClick={openConfirmation}
            disabled={!validSendAddress || !canNominate || isAddressNeedsConfirmation}
          >
            {t('components.governance.modal.nomination.button', 'Nominate')}
          </RootButton>
        </Stack>
      </Stack>
      <ModalSkeleton
        opened={openedConfirmation}
        onClose={
          isReady && !isLoading && !error ? handleCloseConfirmation : handleCloseConfirmationCancel
        }
        title="Confirmation"
      >
        {error && (
          <ErrorMessage
            message={t(
              'components.governance.modal.nominate.errorMessage',
              'Error while nominating'
            )}
            details={error}
          />
        )}
        {isLoading && (
          <ConfirmationLoading
            title={t(
              'components.governance.modal.nominate.loadingMessage',
              'Validating Nomination'
            )}
          />
        )}
        {isReady && !isLoading && !error && (
          <ConfirmationReady
            message={t('components.governance.modal.nominate.readyMessage', 'Nomination Submitted')}
            success={handleCloseConfirmation}
          />
        )}
        {!isReady && !error && !isLoading && (
          <>
            <Stack>
              <Text>
                {t(
                  'components.governance.modal.nominate.confirmMessage',
                  'You are about to nominate this user for the upcoming election. This action is irreversible.'
                )}
              </Text>
              <UserDetails userId={recipientAddressRef.current || ''} />
            </Stack>
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton onClick={handleCloseConfirmationCancel} secondary style1={false} expand>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                disabled={!validSendAddress}
                onClick={handleNominate}
                style1={false}
                expand
              >
                {t('buttons.confirm', 'Confirm')}
              </RootButton>
            </Stack>
          </>
        )}
      </ModalSkeleton>
    </>
  )
}

export default NominateModal
